export const KNOWN_BANNER_TYPES = [
  'small_banner',
  'large_banner',
  'square_banner',
] as const;

export type KnownBannerType = (typeof KNOWN_BANNER_TYPES)[number];

export const FREE_FORM_BANNER_TYPE = 'free_form_banner';

export const BANNER_TYPE_DEFAULTS: Record<
  KnownBannerType,
  { width: number; height: number; aspectRatio: number }
> = {
  small_banner: { width: 413, height: 284, aspectRatio: 1.4 },
  large_banner: { width: 620, height: 427, aspectRatio: 620 / 427 },
  square_banner: { width: 500, height: 500, aspectRatio: 1 },
};

export type MarketingFramePresetId =
  | 'instagram_post'
  | 'instagram_story'
  | 'instagram_portrait'
  | 'facebook_post'
  | 'facebook_cover'
  | 'whatsapp_status'
  | 'youtube_thumbnail'
  | 'linkedin_post'
  | 'twitter_post'
  | 'pinterest_pin'
  | 'tiktok_video'
  | 'custom';

export type BannerFramePresetId = MarketingFramePresetId;

export const DEFAULT_FREE_FORM_PRESET: MarketingFramePresetId = 'instagram_post';

export const FREE_FORM_FRAME_MIN = 100;
export const FREE_FORM_FRAME_MAX = 4096;

export const MARKETING_FRAME_PRESETS: Array<{
  id: MarketingFramePresetId;
  labelKey: string;
  shortLabelKey: string;
  width: number;
  height: number;
  aspectRatio: number;
  groupKey?: string;
}> = [
  {
    id: 'instagram_post',
    labelKey: 'bannerFrameInstagramPost',
    shortLabelKey: 'bannerFrameInstagramPostShort',
    width: 1080,
    height: 1080,
    aspectRatio: 1,
    groupKey: 'bannerFrameGroupInstagram',
  },
  {
    id: 'instagram_story',
    labelKey: 'bannerFrameInstagramStory',
    shortLabelKey: 'bannerFrameInstagramStoryShort',
    width: 1080,
    height: 1920,
    aspectRatio: 1080 / 1920,
    groupKey: 'bannerFrameGroupInstagram',
  },
  {
    id: 'instagram_portrait',
    labelKey: 'bannerFrameInstagramPortrait',
    shortLabelKey: 'bannerFrameInstagramPortraitShort',
    width: 1080,
    height: 1350,
    aspectRatio: 1080 / 1350,
    groupKey: 'bannerFrameGroupInstagram',
  },
  {
    id: 'facebook_post',
    labelKey: 'bannerFrameFacebookPost',
    shortLabelKey: 'bannerFrameFacebookPostShort',
    width: 1200,
    height: 630,
    aspectRatio: 1200 / 630,
    groupKey: 'bannerFrameGroupFacebook',
  },
  {
    id: 'facebook_cover',
    labelKey: 'bannerFrameFacebookCover',
    shortLabelKey: 'bannerFrameFacebookCoverShort',
    width: 1640,
    height: 859,
    aspectRatio: 1640 / 859,
    groupKey: 'bannerFrameGroupFacebook',
  },
  {
    id: 'whatsapp_status',
    labelKey: 'bannerFrameWhatsappStatus',
    shortLabelKey: 'bannerFrameWhatsappStatusShort',
    width: 1080,
    height: 1920,
    aspectRatio: 1080 / 1920,
    groupKey: 'bannerFrameGroupWhatsapp',
  },
  {
    id: 'youtube_thumbnail',
    labelKey: 'bannerFrameYoutubeThumbnail',
    shortLabelKey: 'bannerFrameYoutubeThumbnailShort',
    width: 1280,
    height: 720,
    aspectRatio: 1280 / 720,
    groupKey: 'bannerFrameGroupVideo',
  },
  {
    id: 'tiktok_video',
    labelKey: 'bannerFrameTiktokVideo',
    shortLabelKey: 'bannerFrameTiktokVideoShort',
    width: 1080,
    height: 1920,
    aspectRatio: 1080 / 1920,
    groupKey: 'bannerFrameGroupVideo',
  },
  {
    id: 'linkedin_post',
    labelKey: 'bannerFrameLinkedinPost',
    shortLabelKey: 'bannerFrameLinkedinPostShort',
    width: 1200,
    height: 627,
    aspectRatio: 1200 / 627,
    groupKey: 'bannerFrameGroupLinkedin',
  },
  {
    id: 'twitter_post',
    labelKey: 'bannerFrameTwitterPost',
    shortLabelKey: 'bannerFrameTwitterPostShort',
    width: 1600,
    height: 900,
    aspectRatio: 1600 / 900,
    groupKey: 'bannerFrameGroupTwitter',
  },
  {
    id: 'pinterest_pin',
    labelKey: 'bannerFramePinterestPin',
    shortLabelKey: 'bannerFramePinterestPinShort',
    width: 1000,
    height: 1500,
    aspectRatio: 1000 / 1500,
    groupKey: 'bannerFrameGroupPinterest',
  },
];

export const getMarketingFramePreset = (
  presetId?: string | null,
): (typeof MARKETING_FRAME_PRESETS)[number] | null => {
  if (!presetId || presetId === 'custom') {
    return null;
  }
  return (
    MARKETING_FRAME_PRESETS.find((preset) => preset.id === presetId) ?? null
  );
};

export const isKnownBannerType = (type?: string | null): type is KnownBannerType => {
  if (!type) {
    return false;
  }
  return KNOWN_BANNER_TYPES.includes(
    String(type).toLowerCase() as KnownBannerType,
  );
};

/** True when frame should follow marketing/custom presets — not fixed Respark banner types. */
export const isFreeFormBannerMode = (type?: string | null): boolean => {
  if (!type) {
    return true;
  }
  const key = String(type).toLowerCase();
  if (key === FREE_FORM_BANNER_TYPE) {
    return true;
  }
  return !isKnownBannerType(key);
};

export const normalizeBannerType = (type?: string | null): string => {
  if (!type) {
    return FREE_FORM_BANNER_TYPE;
  }
  const key = String(type).toLowerCase();
  if (isFreeFormBannerMode(key) && key !== FREE_FORM_BANNER_TYPE && !isKnownBannerType(key)) {
    return FREE_FORM_BANNER_TYPE;
  }
  return key;
};

export const getBannerTypeDefaults = (type: string) => {
  const key = String(type).toLowerCase();
  if (isKnownBannerType(key)) {
    return BANNER_TYPE_DEFAULTS[key];
  }
  return null;
};

export const getInitialBgForType = (
  type: string,
  backgrounds: { initial_bg?: Record<string, string> },
  fallback: string,
): string => backgrounds?.initial_bg?.[type] || fallback;

export const clampFreeFormFrameDimension = (value: number): number =>
  Math.min(
    FREE_FORM_FRAME_MAX,
    Math.max(FREE_FORM_FRAME_MIN, Math.round(Number(value) || 0)),
  );
